/**
 * Which table cards a reader has expanded out of their collapsed "Rows"
 * drawer, remembered per Lens.
 *
 * A table card sits behind a collapsed summary strip by default so a board
 * reads as a briefing, not a raw data dump. A reader who opens one to check
 * the rows should not have to reopen it on every visit. This is deliberately
 * local, the same reasoning as `lensVisits.ts`: a personal reading habit, not
 * shared board state, so it is never written to the reporting service.
 *
 * Every read and every write is guarded. Private browsing, a managed browser
 * with site data blocked, and a full quota all throw here, and none of them
 * may cost a reader the board.
 */

const STORAGE_KEY_PREFIX = 'askRichard:lens-rows-expanded:v1:';

function storageKey(lensId: string): string {
  return `${STORAGE_KEY_PREFIX}${lensId}`;
}

export function readExpandedRowsCardIds(lensId: string): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(storageKey(lensId));
    if (!raw) {
      return new Set();
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((id): id is string => typeof id === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

export function writeExpandedRowsCardIds(lensId: string, cardIds: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(storageKey(lensId), JSON.stringify([...cardIds]));
  } catch {
    // Guarded: private browsing, a blocked store or a full quota must not cost a reader the board.
  }
}
